import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Helmet } from "react-helmet-async";
import Landing from "./pages/Landing";
// Lazy-load secondary routes so the landing page bundle stays minimal.
// Each of these pages pulls in heavy deps (jsPDF, html2canvas, Spotify SDK,
// AI generator) that the homepage visitor doesn't need on first paint.
const Builder = lazy(() => import("./pages/Builder"));
const MusicBingo = lazy(() => import("./pages/MusicBingo"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./pages/PaymentCancel"));
const PlayView = lazy(() => import("./pages/PlayView"));
const HostView = lazy(() => import("./pages/HostView"));
const BigScreen = lazy(() => import("./pages/BigScreen"));
// Trust / E-E-A-T pages — small bundles, still lazy so they don't
// bloat the landing page.
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Admin = lazy(() => import("./pages/Admin"));
const CallerApp = lazy(() => import("./pages/CallerApp"));
const CallerTv = lazy(() => import("./pages/CallerTv"));
const Testers = lazy(() => import("./pages/Testers"));
import { ensureClientId } from "./lib/client";
import { I18nProvider } from "./lib/i18n";
import { trackEvent } from "./lib/analytics";

// Detected synchronously at module load: Capacitor injects window.Capacitor
// before our bundle runs, so we can decide the native start route without
// first painting the website homepage (prevents the brief "website flash").
const IS_NATIVE = (() => {
  const cap = typeof window !== "undefined" ? window.Capacitor : undefined;
  return !!cap && (
    (typeof cap.isNativePlatform === "function" && cap.isNativePlatform()) ||
    cap.isNative === true ||
    (typeof cap.getPlatform === "function" && cap.getPlatform() !== "web") ||
    (cap.platform && cap.platform !== "web")
  );
})();

// SPA page_view tracker. GA4 config has send_page_view:false, so we send a
// page_view on every client-side route change (and the first render). We use
// pathname only — never the query string — so tokens/emails on links like
// /edit?token=... never reach analytics (privacy).
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname;
    trackEvent("page_view", {
      page_path: path,
      page_location: window.location.origin + path,
      page_title: typeof document !== "undefined" ? document.title : "",
    });
  }, [location.pathname]);
  return null;
}

// Any route whose URL carries a faceted/tracking query param (?theme=, ?ai=,
// ?category=, ?source=) is a thin duplicate of its clean base route. We mark it
// noindex,follow and point the canonical at the clean path so Google keeps only
// the base page in its index. These URLs are also excluded from the sitemap.
const CANONICAL_STRIP_PARAMS = ["theme", "ai", "category", "source"];
function QueryParamSeo() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (!CANONICAL_STRIP_PARAMS.some((k) => params.has(k))) return null;
  return (
    <Helmet>
      <meta name="robots" content="noindex,follow" />
      <link rel="canonical" href={`https://bingocards-generator.com${location.pathname}`} />
    </Helmet>
  );
}

// Lightweight route fallback — keeps Cumulative Layout Shift low by
// reserving the full viewport rather than collapsing to 0 height.
function RouteFallback() {
  return <div style={{ minHeight: "60vh" }} aria-busy="true" />;
}

// When bundled as a native app (Capacitor/Android), open the Bingo Caller
// first instead of the website homepage. No effect in a normal browser.
function NativeCallerRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const cap = typeof window !== "undefined" ? window.Capacitor : undefined;
    const isNative = !!cap && (
      (typeof cap.isNativePlatform === "function" && cap.isNativePlatform()) ||
      cap.isNative === true ||
      (typeof cap.getPlatform === "function" && cap.getPlatform() !== "web") ||
      (cap.platform && cap.platform !== "web")
    );
    if (isNative && (location.pathname === "/" || location.pathname === "")) {
      navigate("/caller", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function App() {
  useEffect(() => {
    ensureClientId();
    // Owner-only unlock via secret URL (?owner=<ADMIN_TOKEN>): grants
    // unlimited PRO + PRO+ for this browser so the owner can host their own
    // bingos + Spotify for free on mobile (where dev keyboard shortcuts are
    // unavailable). Token is verified server-side, then stripped from the URL.
    try {
      const params = new URLSearchParams(window.location.search);
      const ownerToken = params.get("owner");
      if (ownerToken) {
        import("./lib/client").then(({ ownerUnlock }) =>
          ownerUnlock(ownerToken)
            .then(() => import("sonner").then(({ toast }) => toast.success("Eigenaar-modus actief — PRO+ ontgrendeld op dit apparaat.")))
            .catch(() => import("sonner").then(({ toast }) => toast.error("Ongeldige eigenaar-code.")))
        );
        params.delete("owner");
        const rest = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (rest ? `?${rest}` : ""));
      }
      // Affiliate referral (?ref=CODE): remember the partner for this browser
      // so a later purchase credits their commission.
      const refCode = params.get("ref");
      if (refCode) {
        import("./lib/client").then(({ affiliateTrack }) => affiliateTrack(refCode).catch(() => {}));
        params.delete("ref");
        const rest2 = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (rest2 ? `?${rest2}` : ""));
      }
      // App-store arrival: the Android app CTA carries ?utm_source=android_app
      // (and ref=APP). Log it so the owner sees when app users come in.
      const utm = params.get("utm_source");
      if ((utm && utm.toLowerCase() === "android_app") || (refCode && refCode.toUpperCase() === "APP")) {
        import("./lib/client").then(({ logAppVisit }) =>
          logAppVisit({ source: utm || "android_app", ref: (refCode || "APP").toUpperCase(), path: window.location.pathname }).catch(() => {})
        );
      }
      // Clean any utm_* params from the URL.
      let changed = false;
      ["utm_source", "utm_medium", "utm_campaign"].forEach((k) => { if (params.has(k)) { params.delete(k); changed = true; } });
      if (changed) {
        const rest3 = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (rest3 ? `?${rest3}` : ""));
      }
    } catch { /* ignore */ }
    // Global dev shortcuts — toggle window.__PRO_PLUS_DEV__ which exposes
    // dev unlock buttons in modals (Pro+, Spotify).
    //   Ctrl+Shift+Alt+B  → general dev tools (Pro+ activate, etc.)
    //   Ctrl+Shift+Alt+S  → same flag, kept as a separate shortcut so users
    //     can remember a Spotify-specific one. Both ultimately flip the
    //     same global flag. We dispatch a custom event so components that
    //     read the flag (SpotifyPlayerUI, ProPlusModal) can re-render.
    try {
      window.__PRO_PLUS_DEV__ = localStorage.getItem("bingo_dev_tools") === "1";
    } catch { /* ignore */ }
    const onKey = (e) => {
      if (!(e.ctrlKey && e.shiftKey && e.altKey)) return;
      const k = (e.key || "").toLowerCase();
      if (k === "b" || k === "s") {
        const next = !(window.__PRO_PLUS_DEV__);
        window.__PRO_PLUS_DEV__ = next;
        try { localStorage.setItem("bingo_dev_tools", next ? "1" : "0"); } catch { /* ignore */ }
        window.dispatchEvent(new CustomEvent("bingo:dev-tools-toggle", { detail: { enabled: next } }));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <I18nProvider>
      <div className="App">
        <BrowserRouter>
          <NativeCallerRedirect />
          <RouteTracker />
          <QueryParamSeo />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={IS_NATIVE ? <Navigate to="/caller" replace /> : <Landing />} />
              <Route path="/create" element={<Builder />} />
              {/* /edit?code=X&token=Y is the one-time edit link from the
                  confirmation email; same Builder, deep-link logic handles it. */}
              <Route path="/edit" element={<Builder />} />
              <Route path="/music-bingo" element={<MusicBingo />} />
              <Route path="/play/:code" element={<PlayView />} />
              <Route path="/host/:code" element={<HostView />} />
              <Route path="/screen/:code" element={<BigScreen />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/caller" element={<CallerApp />} />
              <Route path="/caller/tv/:code" element={<CallerTv mode="display" />} />
              <Route path="/caller/control/:code" element={<CallerTv mode="control" />} />
              <Route path="/caller/control" element={<CallerTv mode="control" />} />
              <Route path="/testers" element={<Testers />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </I18nProvider>
  );
}

export default App;
